import React, { Component } from "react";
import classnames from "classnames";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { NavLink, useLocation } from "react-router-dom";
import type { Location } from "react-router-dom";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Auth0Context } from "utils/react-auth0-wrapper";
import { isIndividualAccountOverview } from "store/selectors/user";
import type { RootState } from "types/state";

import "components/navbar/SubNav.scss";

/**
 * Shape of the route-config objects SubNav iterates over. They originate in
 * `pages/dashboards/Routes`; only the fields this component reads are modeled.
 * Looser than `routes/renderDashboardRoutes.DashboardRoute` because SubNav also
 * reads `redirect`/`hidden`/`name` that layer doesn't.
 */
interface SubNavRoute {
  path: string;
  name?: string;
  redirect?: boolean;
  hidden?: boolean;
}

interface SubNavOwnProps {
  location?: Location;
  routes: SubNavRoute[];
}

const mapStateToProps = (state: RootState) => {
  return {
    isAccountOverview: isIndividualAccountOverview(state),
  };
};

const mapDispatchToProps = {
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type SubNavProps = PropsFromRedux & SubNavOwnProps;

// component never sets state beyond the empty initial `{}`.
type SubNavState = Record<string, never>;

export class SubNav extends Component<
  WithApolloClient<SubNavProps>,
  SubNavState
> {
  static contextType = Auth0Context;

  // ref callback target assigned in render; never read back.
  subnavWrapper: HTMLDivElement | null = null;

  constructor(props: WithApolloClient<SubNavProps>) {
    super(props);

    this.state = {};
  }

  // this function creates the links
  createLinks = (routes: SubNavRoute[]) => {
    return routes.map((prop, key) => {
      if (prop.redirect || prop.hidden) {
        return null;
      } else {
        return (
          <li className={this.activeRoute(prop.path)} key={key}>
            <NavLink
              to={prop.path}
              className="icon-nav-link"
              // `activeClassName` was removed from NavLink in react-router v7;
              // it is now an inert prop here. Preserved as a no-op rather than
              // migrating to the `className` callback form.
              // @ts-expect-error react-router v7 NavLink has no activeClassName
              activeClassName="active"
            >
              <>
                <div className="subnav-routes">
                  <p className="subnav-name"> {prop.name}</p>
                </div>
              </>
            </NavLink>
          </li>
        );
      }
    });
  };

  // verifies if routeName is the one active (in browser input)
  activeRoute = (routeName: string) => {
    const pathname = this.props.location?.pathname;
    if (!pathname) return "";
    return pathname === routeName ? "active" : "";
  };

  render() {
    return (
      <div
        className={classnames("subnav", {
          ["hide-subnav"]: this.props.isAccountOverview,
        })}
      >
        <div
          className="subnav-wrapper"
          // block body so the callback returns void: a ref callback's return
          // value (other than a cleanup fn) was always discarded by React, so
          // this is behavior-identical to the original `(ref) => (... = ref)`.
          ref={(ref) => {
            this.subnavWrapper = ref;
          }}
        >
          <ul className="subnav-nav">{this.createLinks(this.props.routes)}</ul>
        </div>
      </div>
    );
  }
}

const ConnectedSubNav = connector(withApollo<SubNavProps>(SubNav));

const SubNavWithLocation = (props: SubNavOwnProps) => {
  const location = useLocation();
  return <ConnectedSubNav {...props} location={location} />;
};

export default SubNavWithLocation;
